import { useEffect, useRef, useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import DiagonalLines from "@/components/feature/DiagonalLines";
import HomeFooter from "@/pages/home/components/HomeFooter";
import PageHero from "@/components/feature/PageHero";
import { useNavigate } from "react-router-dom";

const strengths = [
  { num: "01", title: "企画・制作・サポートを一気通貫で提供", icon: "ri-loop-right-line", body: "「作って終わり」では、ビジネスは変わりません。クラプリックスでは、ホームページの制作だけでなく、現状の課題や事業目標をしっかりとお聞きすることから始めます。戦略から実行まで一貫してご支援することで、長期的な成果につなげます。" },
  { num: "02", title: "ブランディングに強いデザイン力", icon: "ri-palette-line", body: "「きれいなサイト」と「売れるサイト」は、まったく別物です。クラプリックスのデザインは、見た目の美しさと事業への貢献を両立することを最優先に考えています。ホームページだけでなくロゴ・名刺・パンフレットまで一貫したブランドイメージを構築できます。" },
  { num: "03", title: "細やかなヒアリングと丁寧なサポート", icon: "ri-customer-service-2-line", body: "「何をお願いすればいいかわからない」という方こそ、ぜひご相談ください。まとまっていない想いや漠然とした課題感でも、丁寧にヒアリングしながら最適な形に整理してご提案します。全国どこからでもオンライン対応可能です。" },
  { num: "04", title: "デジタルマーケティングを総合的に支援", icon: "ri-line-chart-line", body: "SEO対策・MEO対策・リスティング広告・SNS広告・インフルエンサーマーケティングまで、お客様のビジネスに合ったデジタルマーケティングを幅広くご提案します。現状を分析したうえで最適な組み合わせをご提案します。" },
];

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add("revealed"); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

/* ── Approach Section ── */
function ApproachSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="relative py-16 md:py-24 lg:py-32" style={{ background: "linear-gradient(to right, #e8ddd8, #ffffff)" }}>
      <div
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-0">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            {/* 左側：写真 */}
            <div className="hidden md:block md:w-[45%] lg:w-[32%] shrink-0">
              <img
                src="https://readdy.ai/api/search-image?query=Close-up%20editorial%20photograph%20of%20a%20premium%20silver%20laptop%20computer%20on%20a%20clean%20marble%20desk%20with%20soft%20warm%20natural%20window%20light%2C%20modern%20minimalist%20workspace%2C%20warm%20beige%20and%20cream%20tones%2C%20shallow%20depth%20of%20field%2C%20professional%20product%20photography%20style%2C%20warm%20ambient%20lighting%2C%20elegant%20business%20atmosphere%2C%20high%20detail%20and%20texture%2C%20cozy%20creative%20office%20environment&width=600&height=800&seq=strength-laptop-photo-v4&orientation=portrait"
                alt="CRUPLIX creative team"
                className="w-full object-cover"
                style={{ aspectRatio: "3/4" }}
              />
            </div>

            {/* 右側：テキスト */}
            <div className="flex-1 w-full">
              {/* タイトル部分 */}
              <div className="text-left md:text-right mb-10 md:mb-16">
                <p
                  className="font-bold leading-relaxed"
                  style={{
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: "clamp(23px, 2.5vw, 32px)",
                    color: "#111111",
                  }}
                >
                  選ばれる理由が、<br />ここにあります。
                  <span className="inline-block align-middle ml-2" style={{ width: "44px", height: "3px", background: "#c00000" }} />
                </p>
              </div>

              {/* 文章部分 */}
              <div className="text-right w-full">
                <p
                  className="leading-loose mb-6"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(13px, 1.5vw, 17px)", color: "#111111" }}
                >
                  制作会社はたくさんあります。
                </p>
                <p
                  className="leading-loose mb-10"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(13px, 1.5vw, 17px)", color: "#111111" }}
                >
                  でも、作るだけでなく一緒に動いてくれる担当者には<br className="md:hidden" />なかなか巡り合えません。
                </p>
                <p
                  className="leading-loose mb-6"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(13px, 1.5vw, 17px)", color: "#111111" }}
                >
                  CRUPLIXが選ばれるのは、<br className="md:hidden" />デザイン・戦略・集客・サポートを、<br className="md:hidden" />ひとつのチームで完結できるから。
                </p>
                <p
                  className="leading-loose"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "clamp(13px, 1.5vw, 17px)", color: "#111111" }}
                >
                  あなたのビジネスに本当に必要なことを、<br className="md:hidden" />一緒に考えます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Strengths Section ── */
function StrengthsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 lg:py-32" style={{ background: "linear-gradient(to right, #e8ddd8, #ffffff)" }}>
      <div
        className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-0"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(30px)",
          transition: "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          {/* Left: Section heading */}
          <div className="w-full lg:w-[35%] shrink-0 relative" style={{ minHeight: "0px" }}>
            <div className="relative z-10 pt-4">
              <h2
                className="font-bold mb-3"
                style={{
                  fontFamily: "'Barlow', sans-serif",
                  fontSize: "clamp(36px, 5vw, 56px)",
                  color: "#111111",
                  lineHeight: 1.1,
                }}
              >
                CRUPLIX
              </h2>
              <p
                className="font-medium mb-8"
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "clamp(14px, 1.5vw, 18px)",
                  color: "#c00000",
                  letterSpacing: "0.05em",
                }}
              >
                選ばれる4つの理由
              </p>
            </div>
          </div>

          {/* Right: Strength items */}
          <div className="flex-1 flex flex-col gap-12 md:gap-16 lg:gap-24">
            {strengths.map((s) => (
              <div
                key={s.num}
                className="flex flex-col md:flex-row gap-4 md:gap-8 items-start"
              >
                {/* Left: number */}
                <span
                  className="font-light shrink-0"
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 300,
                    fontSize: "clamp(32px, 4vw, 48px)",
                    color: "#c00000",
                    lineHeight: 1,
                    minWidth: "60px",
                  }}
                >
                  {s.num}
                </span>

                {/* Right: title + body */}
                <div className="flex-1">
                  <h3
                    className="font-bold mb-3"
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: "clamp(18px, 2.5vw, 30px)",
                      color: "#111111",
                      lineHeight: 1.3,
                    }}
                  >
                    {s.title}
                  </h3>
                  <p
                    className="leading-loose"
                    style={{
                      fontFamily: "'Noto Sans JP', sans-serif",
                      fontSize: "clamp(11px, 1.2vw, 15px)",
                      color: "#111111",
                    }}
                  >
                    {s.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  const ref = useReveal(0.15);
  const navigate = useNavigate();
  return (
    <section className="py-10 md:py-20 px-4 md:px-6" style={{ background: "linear-gradient(to right, #e8ddd8, #ffffff)" }}>
      <div ref={ref} className="reveal max-w-5xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="text-sm tracking-[0.4em] font-medium" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>CONTACT</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-3" style={{ fontFamily: "'Barlow', sans-serif", color: "#111111" }}>お問い合わせ</h2>
          <div className="mx-auto mt-4" style={{ width: "32px", height: "2px", background: "#c00000" }} />
          <p className="text-xs md:text-sm leading-loose mt-5 mx-auto whitespace-nowrap" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>
            ご不明点などございましたらお気軽にお問い合わせください。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-6">
          <div
            className="flex flex-col items-center text-center p-4 md:p-8 cursor-pointer"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onClick={() => navigate("/contact")}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#c00000"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#f0f0f0" }}>
              <i className="ri-mail-line text-base md:text-xl" style={{ color: "#111111" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>MAIL</h3>
            <span className="text-xs md:text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#c00000" }}>
              <span className="hidden md:inline">フォームを開く</span> <i className="ri-arrow-right-line" />
            </span>
          </div>
          <a
            href="https://lin.ee/tP9rADP"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center text-center p-4 md:p-8"
            style={{ background: "#ffffff", border: "1px solid #e0e0e0", transition: "border-color 0.3s, transform 0.3s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#06C755"; e.currentTarget.style.transform = "translateY(-4px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "#e0e0e0"; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mb-2 md:mb-4" style={{ background: "#06C755" }}>
              <i className="ri-line-fill text-base md:text-xl" style={{ color: "#ffffff" }} />
            </div>
            <h3 className="font-bold text-sm md:text-base mb-1 md:mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif", color: "#111111" }}>LINE</h3>
            <span className="text-xs md:text-sm font-medium flex items-center gap-1" style={{ fontFamily: "'Barlow', sans-serif", color: "#06C755" }}>
              <span className="hidden md:inline">LINEを開く</span> <i className="ri-external-link-line" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function StrengthPage() {
  return (
    <PageLayout>
      <ApproachSection />
      <StrengthsSection />
      <CtaSection />
      <HomeFooter />
      <DiagonalLines variant="gold" intensity="light" />
    </PageLayout>
  );
}